import React, { useState, useEffect, useRef } from 'react';
import { executeGetujibunseki, executeGetujibunsekiReaggregate } from '../api/api';

const FISCAL_MONTHS = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2];
const REAGGREGATE_MIN_FISCAL_YEAR = 25;
const REAGGREGATE_MAX_MONTHS = 3;

/** バックエンドと同じ基準で現在の年度（YY形式）を返す */
function getCurrentFiscalYearYY(date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  if (month < 3) {
    return String(year - 1).slice(-2);
  }
  return String(year).slice(-2);
}

/** 再集計で選択可能な年度一覧（新しい年度が先頭） */
function getFiscalYearOptions(minYear = REAGGREGATE_MIN_FISCAL_YEAR, date = new Date()) {
  const currentYY = parseInt(getCurrentFiscalYearYY(date), 10);
  const years = [];
  for (let yy = currentYY; yy >= minYear; yy -= 1) {
    years.push(String(yy).padStart(2, '0'));
  }
  return years;
}

function GetujibunsekiPage() {
  const fiscalYears = getFiscalYearOptions();
  const [loading, setLoading] = useState(false);
  const [reaggregateLoading, setReaggregateLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [logs, setLogs] = useState([]);
  const [selectedYear, setSelectedYear] = useState(() => getCurrentFiscalYearYY());
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [isReaggregateOpen, setIsReaggregateOpen] = useState(false);
  const isClearingRef = useRef(false);

  // statusがnullになったときに確実にクリア
  useEffect(() => {
    if (status === null && isClearingRef.current) {
      setMessage('');
      setLogs([]);
      isClearingRef.current = false;
    }
  }, [status]);

  const toggleMonth = (month) => {
    setSelectedMonths((prev) => {
      if (prev.includes(month)) {
        return prev.filter((m) => m !== month);
      }
      if (prev.length >= REAGGREGATE_MAX_MONTHS) {
        return prev;
      }
      return [...prev, month];
    });
  };

  const applyBatchResult = (result, options = {}) => {
    setStatus(result.status);

    if (result.logs && Array.isArray(result.logs) && result.logs.length > 0) {
      setLogs(result.logs);
    } else {
      setLogs([]);
    }

    const targetMonthLabel = options.isReaggregate && options.months?.length
      ? `\n【再集計対象: 20${options.fiscalYear}年度 ${options.months.map((m) => `${m}月`).join('、')}】`
      : '';

      if (result.status === 'success') {
        let messageText = options.isReaggregate
          ? '再集計が正常に完了しました。成果物を確認してください。'
          : 'バッチ処理が正常に完了しました。成果物を確認してください。';
        messageText += targetMonthLabel;
      if (result.totalFiles !== undefined && result.processedFiles !== undefined) {
        messageText += `\n【取り込みファイル総数: ${result.totalFiles}件：取り込み完了ファイル数: ${result.processedFiles}件】`;
      }
      if (result.startTime) {
        messageText += `\n実行開始日時: ${result.startTime}`;
      }
      if (result.endTime) {
        messageText += `\n実行終了日時: ${result.endTime}`;
      }
      setMessage(messageText);
    } else if (result.status === 'warning') {
      let messageText = options.isReaggregate
        ? '再集計が完了しましたが、取り込めないデータがあります。'
        : 'バッチ処理が完了しましたが、取り込めないデータがあります。';
      messageText += targetMonthLabel;
      if (result.totalFiles !== undefined && result.processedFiles !== undefined) {
        messageText += `\n【取り込みファイル総数: ${result.totalFiles}件：取り込み完了ファイル数: ${result.processedFiles}件】`;
      }
      if (result.startTime) {
        messageText += `\n実行開始日時: ${result.startTime}`;
      }
      if (result.endTime) {
        messageText += `\n実行終了日時: ${result.endTime}`;
      }
      setMessage(messageText);
    } else if (result.status === 'error') {
      let messageText = 'エラーが発生しました。詳細はログを確認してください。';
      if (result.startTime) {
        messageText += `\n実行開始日時: ${result.startTime}`;
      }
      if (result.endTime) {
        messageText += `\n実行終了日時: ${result.endTime}`;
      }
      setMessage(messageText);
    } else {
      const errorMsg = result.message || '不明なエラー';
      let messageText = `エラーが発生しました: ${errorMsg}`;
      if (result.startTime) {
        messageText += `\n実行開始日時: ${result.startTime}`;
      }
      if (result.endTime) {
        messageText += `\n実行終了日時: ${result.endTime}`;
      }
      setMessage(messageText);
    }
  };

  const handleBatchError = (error) => {
    setStatus('error');
    setLogs([]);
    setMessage('');

    if (error.message && error.message.includes('既に実行中')) {
      setMessage('バッチ処理は既に実行中です。\n他のユーザーが実行中のため、しばらく待ってから再度お試しください。');
    } else if (error.message.includes('接続できません') || error.message.includes('Failed to fetch')) {
      setMessage(`${error.message}\n\nサーバーを起動するには:\n1. macOS/Linux: ./start-local.sh ／ Windows: .\\start-local.ps1\n2. または手動でサーバーを起動`);
    } else {
      setMessage(`エラーが発生しました: ${error.message || '不明なエラー'}`);
    }
  };

  const handleExecute = async () => {
    // 既に実行中の場合は何もしない
    if (loading) {
      return;
    }

    // 前回の実行ログを即座にクリア
    isClearingRef.current = true;
    setIsReaggregateOpen(false);
    setStatus(null);
    setMessage('');
    setLogs([]);
    setLoading(true);

    try {
      const result = await executeGetujibunseki();
      applyBatchResult(result);
    } catch (error) {
      handleBatchError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleReaggregate = async () => {
    if (reaggregateLoading || loading) {
      return;
    }

    if (selectedMonths.length === 0) {
      setStatus('error');
      setLogs([]);
      setMessage('再集計する月を1つ以上選択してください。');
      return;
    }

    if (selectedMonths.length > REAGGREGATE_MAX_MONTHS) {
      setStatus('error');
      setLogs([]);
      setMessage(`再集計は最大${REAGGREGATE_MAX_MONTHS}ヶ月まで選択できます。`);
      return;
    }

    isClearingRef.current = true;
    setStatus(null);
    setMessage('');
    setLogs([]);
    setReaggregateLoading(true);

    try {
      const result = await executeGetujibunsekiReaggregate({
        fiscalYear: selectedYear,
        months: selectedMonths,
      });
      applyBatchResult(result, {
        isReaggregate: true,
        fiscalYear: selectedYear,
        months: selectedMonths,
      });
    } catch (error) {
      handleBatchError(error);
    } finally {
      setReaggregateLoading(false);
    }
  };

  const isBusy = loading || reaggregateLoading;

  return (
    <div className="page-container page-container-wide">
      <section className="batch-section">
        <h1 className="page-title">月次分析表</h1>
        <p className="page-description">
          月次分析表のバッチ処理を実行します
        </p>
        <button
          className="execute-button"
          onClick={() => {
            handleExecute();
          }}
          disabled={isBusy}
        >
          {loading ? '実行中...' : 'バッチ処理を実行'}
        </button>
      </section>

      <div className="section-divider" />

      <section className="reaggregate-accordion">
        <button
          type="button"
          className="reaggregate-accordion-header"
          onClick={() => setIsReaggregateOpen((prev) => !prev)}
          aria-expanded={isReaggregateOpen}
        >
          <span className="reaggregate-accordion-title">過去の数値を再集計</span>
          <span className={`reaggregate-accordion-icon ${isReaggregateOpen ? 'open' : ''}`}>
            ▼
          </span>
        </button>

        {isReaggregateOpen && (
          <div className="reaggregate-accordion-content">
            <p className="reaggregate-description">
              年度と月を選択して実行してください（月は最大{REAGGREGATE_MAX_MONTHS}ヶ月まで）
            </p>

            <div className="reaggregate-controls">
              <label className="year-select-label" htmlFor="fiscal-year-select">
                <select
                  id="fiscal-year-select"
                  className="year-select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  disabled={isBusy}
                >
                  {fiscalYears.map((year) => (
                    <option key={year} value={year}>
                      20{year}年度
                    </option>
                  ))}
                </select>
              </label>

              <div className="month-checkbox-grid">
                {FISCAL_MONTHS.map((month) => {
                  const isChecked = selectedMonths.includes(month);
                  const isDisabledByLimit = isBusy || (!isChecked && selectedMonths.length >= REAGGREGATE_MAX_MONTHS);
                  return (
                  <label
                    key={month}
                    className={`month-checkbox-label${isDisabledByLimit ? ' month-checkbox-label-disabled' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleMonth(month)}
                      disabled={isDisabledByLimit}
                    />
                    <span>{month}月</span>
                  </label>
                  );
                })}
              </div>

              {selectedMonths.length >= REAGGREGATE_MAX_MONTHS && (
                <p className="reaggregate-month-hint">
                  一度に再集計できるのは3ヶ月分を推奨
                </p>
              )}

              <button
                className="execute-button reaggregate-button"
                onClick={() => {
                  handleReaggregate();
                }}
                disabled={isBusy}
              >
                {reaggregateLoading ? '実行中...' : '再集計'}
              </button>
            </div>
          </div>
        )}
      </section>

      {status && (
        <div key={`${status}-${message}-${logs.length}`} className={`status-message ${status}`}>
          {message}
          {logs.length > 0 && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(0,0,0,0.1)' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', textAlign: 'left' }}>ログ:</div>
              <div style={{ maxHeight: '300px', overflowY: 'auto', fontSize: '0.9rem', textAlign: 'left' }}>
                {logs.map((log, index) => (
                  <div 
                    key={index} 
                    style={{ 
                      marginBottom: '0.5rem',
                      padding: '0.5rem',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      textAlign: 'left'
                    }}
                  >
                     <span style={{ color: '#666', marginRight: '0.5rem' }}>■</span>{log}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default GetujibunsekiPage;

